import React, { useState, useRef, useEffect } from 'react';
import {
    makeStyles, Grid,
    Switch, Button,
} from '@material-ui/core';
import { useQuery, useMutation } from '@apollo/client';
import LotsCategory, { LotDefaultskey } from 'utils/enum/LotsCategory';
import { ALL_LOTS, FetchPolicy } from 'utils/enum/Core';
import { Form } from 'react-bootstrap';
import StringUtils from 'lib/StringUtils';
import ModalUtils from 'utils/ModalUtils';
import ArrayUtils from 'lib/ArrayUtils';
import GeneralUtils from 'utils/GeneralUtils';
import LotQuery from 'services/graphQL/query/LotQuery';
import LotMutation from 'services/graphQL/mutate/LotMutation';
import AccountingUtils from 'utils/AccountingUtils';

// icons
import SaveOutlinedIcon from '@material-ui/icons/SaveOutlined';

const useStyles = makeStyles((theme) => ({
    box: {
        marginTop: '10px',
        paddingTop: '10px',
        paddingBottom: '10px',
        '& > div > button': {
            marginRight: '10px',
        },
    },
    header: {
        fontSize: '15px',
        fontWeight: 'bold',
        paddingBottom: '10px',
        borderBottom: `solid 1px ${theme.palette.border.mercury}`,
        marginBottom: '15px',
    },
    wrapper: {
        flexDirection: 'row',
        display: 'flex',
    },
    columnsHeader: {
        display: 'flex',
        fontSize: '14px',
        fontWeight: 'bold',
        marginBottom: '20px',
        '& > div:nth-child(1)': {
            width: '300px',
        },
    },
    column: {
        display: 'flex',
        alignItems: 'center',
        fontSize: '14px',
        '& > div:nth-child(1)': {
            width: '300px',
        },
    },
    balanceDue: {
        marginTop: '30px',
        '& > div:nth-child(1)': {
            fontSize: '14px',
            fontWeight: 'bold',
            marginBottom: '20px',
        },
        '& > div:nth-child(2)': {
            display: 'flex',
            flexDirection: 'row',
            '& > div:nth-child(1)': {
                width: '200px',
                flexShrink: 0,
                border: `1px solid ${theme.palette.border.ghost}`,
                borderRadius: '5px',
                height: 'auto',
                overflowY: 'auto',
                overflowX: 'hidden',
                '& > div': {
                    fontSize: '14px',
                    fontWeight: 300,
                    marginBottom: '5px',
                    borderBottom: `1px solid ${theme.palette.border.ghost}`,
                    padding: '5px',
                    cursor: 'pointer',
                },
            },
            '& > div:nth-child(2)': {
                flexGrow: 1,
                marginLeft: '10px',
                '& > textarea': {
                    resize: 'none',
                },
            },
        },
    },
    save: {
        position: 'absolute',
        bottom: 0,
        right: 0,
    },
    rightSide: {
        position: 'relative',
    },
}));

const AccountsReceivableSettings = () => {
    const classes = useStyles();
    const formulaRef = useRef();
    const [state, setState] = useState({
        noDisplayTransferredDeals: false,
        noDisplayCashDeals: false,
        noDisplayWholesaleDeals: false,
        balanceFormula: null,
        columns: AccountingUtils.getDefaultAccountsReceivableColumns(),
    });

    const {
        columns,
        noDisplayTransferredDeals,
        noDisplayCashDeals,
        noDisplayWholesaleDeals,
        balanceFormula,
    } = state;

    const variablesForFormula = ['Balance Due', 'Reserve', 'Discount'];
    const { ACCOUNT_RECEIVABLE_DISPLAY_SETTINGS } = LotDefaultskey;
    const {
        data: settings,
        loading: settingsLoading,
        error: settingsError,
    } = useQuery(LotQuery.GET_SETTINGS, {
        variables: {
            category: LotsCategory.ACCOUNTING,
            lotName: ALL_LOTS,
            key: [ACCOUNT_RECEIVABLE_DISPLAY_SETTINGS],
        },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const [saveSettings, { loading: isSavingSettings }] = useMutation(LotMutation.SAVE_LOT_DEFAULTS, {
        onCompleted: (response) => {
            if (response) ModalUtils.successMessage(null, 'Settings saved successfully');
        },
        onError: (errorMessage) => {
            ModalUtils.errorMessage([errorMessage]);
        },
    });

    useEffect(() => {
        if (settingsError) {
            ModalUtils.errorMessage(settingsError?.graphQLErrors);
            return;
        }

        if (!settingsLoading && settings) {
            const { getSettings } = settings;
            if (ArrayUtils.isNotEmpty(getSettings)) {
                const displaySettings = getSettings.find((setting) => setting.key === ACCOUNT_RECEIVABLE_DISPLAY_SETTINGS)?.value ?? '';
                if (!StringUtils.isEmpty(displaySettings)) {
                    try {
                        setState(JSON.parse(displaySettings));
                    } catch (error) {
                        ModalUtils.errorMessage(['Error loading display settings']);
                    }
                }
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settingsLoading, settingsError]);

    const onUpateColumn = (title, value) => {
        setState((prevState) => ({
            ...prevState,
            columns: prevState.columns.map((c) => {
                if (c.title === title) return { ...c, display: value };

                return c;
            }),
        }));
    };

    const onChange = (name, value) => {
        setState((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const insertFieldInFormula = (variable) => {
        const focusedElement = document.activeElement;

        let clone = (balanceFormula || '').repeat(1);
        const { selectionStart = 0, selectionEnd = 0 } = focusedElement;
        const anySelection = selectionStart !== selectionEnd;

        if (anySelection) clone = clone.replace(clone.substring(selectionStart, selectionEnd), variable);
        if (!anySelection) clone = StringUtils.insertAt(clone, variable, selectionStart);
        onChange('balanceFormula', clone);
    };

    const onSave = () => {
        if (
            !StringUtils.isEmpty(balanceFormula)
            && !GeneralUtils.validateFormula(
                variablesForFormula.map((v) => ({
                    variable: v,
                    value: 0,
                })),
                balanceFormula,
            )
        ) {
            ModalUtils.errorMessage(null, 'Please check the balance due formula before saving');
            return;
        }

        const payload = JSON.stringify({
            columns,
            noDisplayTransferredDeals,
            noDisplayCashDeals,
            noDisplayWholesaleDeals,
            balanceFormula,
        });

        saveSettings({
            variables: {
                category: LotsCategory.ACCOUNTING,
                key: ACCOUNT_RECEIVABLE_DISPLAY_SETTINGS,
                value: payload,
                lotName: ALL_LOTS,
                critical: false,
            },
        });
    };

    return (
        <Grid container className={classes.box}>
            <Grid item xs={12}>
                <div className={classes.header}>
                    Display Settings
                </div>
            </Grid>
            <Grid item xs={12} className={classes.wrapper}>
                <Grid item md={6} xs={12}>
                    <Grid item md={6} xs={12}>
                        <div className={classes.columnsHeader}>
                            <div>Column Headers</div>
                            <div>Display</div>
                        </div>
                    </Grid>
                    <Grid item md={6} xs={12}>
                        {columns.map((c, index) => (
                            <div key={index} className={classes.column}>
                                <div>{c.title}</div>
                                <Switch
                                    checked={c.display}
                                    onChange={(event) => onUpateColumn(c.title, event.target.checked)}
                                    inputProps={{ 'aria-label': 'primary checkbox' }}
                                />
                            </div>
                        ))}
                    </Grid>
                </Grid>
                <Grid item md={6} xs={12} className={classes.rightSide}>
                    <div className={classes.column}>
                        <div>Do Not Display Transferred Deals</div>
                        <Switch
                            checked={noDisplayTransferredDeals}
                            onChange={(event) => onChange('noDisplayTransferredDeals', event.target.checked)}
                            inputProps={{ 'aria-label': 'primary checkbox' }}
                        />
                    </div>
                    <div className={classes.column}>
                        <div>Do Not Display Cash Deals</div>
                        <Switch
                            checked={noDisplayCashDeals}
                            onChange={(event) => onChange('noDisplayCashDeals', event.target.checked)}
                            inputProps={{ 'aria-label': 'primary checkbox' }}
                        />
                    </div>
                    <div className={classes.column}>
                        <div>Do Not Display Wholesale Deals</div>
                        <Switch
                            checked={noDisplayWholesaleDeals}
                            onChange={(event) => onChange('noDisplayWholesaleDeals', event.target.checked)}
                            inputProps={{ 'aria-label': 'primary checkbox' }}
                        />
                    </div>
                    <div className={classes.balanceDue}>
                        <div>
                            Balance Due Formula
                        </div>
                        <div>
                            <div>
                                {variablesForFormula.map((name, index) => (
                                    <div
                                        key={index}
                                        onMouseDown={(event) => {
                                            event.preventDefault();
                                            insertFieldInFormula(name);
                                        }}
                                    >
                                        {name}
                                    </div>
                                ))}
                            </div>
                            <div>
                                <Form.Control
                                    ref={formulaRef}
                                    as="textarea"
                                    size="sm"
                                    rows="10"
                                    name="balanceFormula"
                                    value={balanceFormula ?? ''}
                                    onChange={(e) => onChange(e.target.name, e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                    <div className={classes.save}>
                        <Button
                            variant="outlined"
                            startIcon={<SaveOutlinedIcon />}
                            size="small"
                            disabled={isSavingSettings}
                            onClick={onSave}
                        >
                            Save
                        </Button>
                    </div>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default AccountsReceivableSettings;
